/**
 * @component ry-slider
 * @category  form
 * @summary   Single- or dual-handle numeric slider (Fomantic UI `.ui.slider` equivalent). Use `value` for single, `start`+`end` with `[range]` for dual-handle range. Drag-fire is `ry:input`; release-fire is `ry:change`. Keyboard arrows step by `step`; PageUp/Down jump 10% of the range; Home/End jump to extremes. `[vertical]` flips orientation; `[reversed]` flips the direction.
 *
 * Reference: Fomantic UI's slider module. ry-slider supports the same range +
 * labels + tooltip + step combinations. happy-dom cannot simulate pointer
 * drag layout, so tests cover keyboard / programmatic / event paths.
 *
 * @attribute min        number?   default=0     Minimum value.
 * @attribute max        number?   default=100   Maximum value.
 * @attribute step       number?   default=1     Step increment. Used by arrow keys + pointer snapping. Set to `0` to disable snapping.
 * @attribute value      number?   default=min   Single-thumb value (used when `[range]` is absent).
 * @attribute start      number?   default=min   Range mode: lower thumb. Honored only when `[range]` is set.
 * @attribute end        number?   default=max   Range mode: upper thumb.
 * @attribute range      boolean   default=false   Enables dual-handle range mode. Switches the rendered markup to two thumbs.
 * @attribute labeled    boolean   default=false   Renders a labels row below the track (5 evenly distributed values from min to max, or custom via [labels]).
 * @attribute labels     string?              Comma-separated custom label strings. Overrides the auto-generated 5-label distribution.
 * @attribute label-type 'number' | 'letter'?  default="number"   When auto-generating labels: "number" rounds floats; "letter" emits A..E.
 * @attribute tooltip    boolean   default=false   Show the current value above each thumb while dragging / focused.
 * @attribute vertical   boolean   default=false   Flip orientation to vertical (bottom = min, top = max).
 * @attribute reversed   boolean   default=false   Reverse the visual direction (max appears at the start of the track).
 * @attribute disabled   boolean   default=false   Non-interactive — pointer + keyboard suppressed.
 *
 * @property  min        number    Live numeric min.
 * @property  max        number    Live numeric max.
 * @property  step       number    Live numeric step (0 disables snapping).
 * @property  value      number    Single-handle value, clamped to [min, max] on set.
 * @property  start      number    Range lower-handle, clamped.
 * @property  end        number    Range upper-handle, clamped.
 * @property  disabled   boolean   Live disabled state.
 *
 * @event ry:input
 *        detail: { value: number } | { start: number, end: number }
 *        fires:  on every micro-step during drag AND on every keyboard step. Use for live previews (no debounce).
 *
 * @event ry:beforechange
 *        detail: { value: number, previous: number } | { start: number, end: number, previous: { start: number, end: number } }
 *        fires:  before each commit — at the top of the keyboard step (before the value moves) and on pointer release (the value has already moved live during the drag; `previous` is the value at drag start). `ry:input` is the live stream and is NOT intercepted.
 *        cancelable: preventDefault() cancels the commit — keyboard: the value does not move; pointer: the thumb snaps back to `previous` (one trailing `ry:input` announces the revert). `ry:change` does not fire either way.
 *
 * @event ry:change
 *        detail: { value: number } | { start: number, end: number }
 *        fires:  on pointer release AND on every keyboard step. Use this for commit-after-drag patterns. For continuous keyboard control, both `ry:input` and `ry:change` fire on each keystroke.
 *
 * @keyboard  ArrowRight / ArrowUp — increment by `step` (decrement if `[reversed]`). ArrowLeft / ArrowDown — decrement by `step`. PageUp — jump up by 10% of range. PageDown — jump down by 10%. Home — set to min. End — set to max. All keyboard events fire `ry:input` AND `ry:change`. Disabled: all suppressed.
 *
 * @state     None on the host. Thumbs carry `role="slider"`, `aria-valuemin/now/max`, `aria-label`, and `tabindex="0"`.
 *
 * @content   None — `<ry-slider>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom (single)
 *   ry-slider[min][max][step][value][disabled?]
 *     > div.ry-slider__track    [data-ry-target="track"]
 *       > div.ry-slider__fill   [data-ry-target="fill"]
 *       > div.ry-slider__thumb  [data-ry-target="thumb-value"]   role="slider"  tabindex="0"  aria-valuenow=value  aria-valuemin=min  aria-valuemax=max
 *         > span.ry-slider__tooltip  [data-ry-target="tooltip-value"]   (only when [tooltip])
 *     > div.ry-slider__labels   [data-ry-target="labels"]   (only when [labeled])
 *
 * @dom (range)
 *   Adds a SECOND thumb element [data-ry-target="thumb-end"]. The start thumb's aria-valuemax shrinks to `end`; the end thumb's aria-valuemin grows to `start` (per WAI-ARIA range slider pattern).
 *
 * @gotcha  In range mode, `aria-valuemin` and `aria-valuemax` on the start/end thumbs DO NOT match the slider's overall min/max — they reflect the OTHER thumb's value to prevent crossover. This is correct per WAI-ARIA; consumers reading the attributes for general bounds need to query `[start]`/`[end]` from the host.
 *
 * @gotcha  `ry:change` fires on EVERY keyboard step (in addition to `ry:input`). This may surprise consumers expecting `change` to mean "user released the mouse" — for keyboard, there is no separate "release" so we fire both. For input-only debouncing, listen to `ry:input` and ignore `ry:change`.
 *
 * @gotcha  `step` of `0` disables snapping; pointer values are clamped but not rounded. Useful for high-precision use cases (e.g. color channel sliders). Default step `1` snaps to integers.
 *
 * @gotcha  Drag/pointer behavior requires real layout (`getBoundingClientRect`) and is NOT testable in happy-dom — vitest tests cover the programmatic + keyboard path only. Cover drag interactions with Playwright if needed.
 *
 * @gotcha  Setting `start` greater than `end` (or vice versa) via the property setter would create a crossed range. The drag and keyboard paths enforce no-crossover via `Math.min` / `Math.max`; programmatic setters trust the consumer. Validate before setting if necessary.
 *
 * @example single
 * ```html
 * <ry-slider min="0" max="100" value="50"></ry-slider>
 * ```
 *
 * @example range
 * ```html
 * <ry-slider min="0" max="100" start="25" end="75" range></ry-slider>
 * ```
 *
 * @example with-labels
 * ```html
 * <ry-slider min="0" max="100" value="40" labeled></ry-slider>
 * ```
 *
 * @example custom-labels
 * ```html
 * <ry-slider min="1" max="5" step="1" value="3" labeled labels="poor,fair,good,great,best"></ry-slider>
 * ```
 *
 * @example with-tooltip
 * ```html
 * <ry-slider min="0" max="100" value="50" tooltip></ry-slider>
 * ```
 *
 * @example vertical
 * ```html
 * <ry-slider min="0" max="100" value="50" vertical></ry-slider>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-slider min="0" max="100" value="50" disabled></ry-slider>
 * ```
 *
 * @tutorial mount-renders-single-thumb
 * ```js
 * const el = mount('<ry-slider min="0" max="100" value="50"></ry-slider>');
 * await expectEqual(!!el.querySelector('[data-ry-target="track"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="thumb-value"]'), true);
 * await expectEqual(el.querySelector('[data-ry-target="thumb-start"]'), null, 'no start thumb in single mode');
 * ```
 *
 * @tutorial range-mode-renders-two-thumbs
 * ```js
 * const el = mount('<ry-slider min="0" max="100" start="25" end="75" range></ry-slider>');
 * await expectEqual(!!el.querySelector('[data-ry-target="thumb-start"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="thumb-end"]'), true);
 * await expectEqual(el.querySelector('[data-ry-target="thumb-value"]'), null);
 * ```
 *
 * @tutorial aria-attributes-mirror-state
 * ```js
 * const el = mount('<ry-slider min="0" max="200" value="80"></ry-slider>');
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * await expectAttr(thumb, 'role', 'slider');
 * await expectAttr(thumb, 'tabindex', '0');
 * await expectAttr(thumb, 'aria-valuenow', '80');
 * await expectAttr(thumb, 'aria-valuemin', '0');
 * await expectAttr(thumb, 'aria-valuemax', '200');
 * ```
 *
 * @tutorial range-aria-prevents-crossover
 * ```js
 * // Wrong assumption: each thumb's aria-valuemin/max matches the slider's
 * // overall bounds. In range mode, the bounds shrink to the OTHER thumb's
 * // value to prevent crossover (per WAI-ARIA range slider pattern).
 * const el = mount('<ry-slider min="0" max="100" start="25" end="75" range></ry-slider>');
 * const startThumb = el.querySelector('[data-ry-target="thumb-start"]');
 * const endThumb = el.querySelector('[data-ry-target="thumb-end"]');
 * await expectAttr(startThumb, 'aria-valuemax', '75', 'start thumb max == end value');
 * await expectAttr(endThumb, 'aria-valuemin', '25', 'end thumb min == start value');
 * ```
 *
 * @tutorial value-setter-clamps
 * ```js
 * const el = mount('<ry-slider min="0" max="100" value="50"></ry-slider>');
 * el.value = 999;
 * await expectEqual(el.value, 100, 'clamped to max');
 * el.value = -10;
 * await expectEqual(el.value, 0, 'clamped to min');
 * ```
 *
 * @tutorial keyboard-arrow-steps-and-fires-events
 * ```js
 * const el = mount('<ry-slider min="0" max="100" step="5" value="50"></ry-slider>');
 * const fired = { input: [], change: [] };
 * el.addEventListener('ry:input', (e) => fired.input.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.value));
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 55, 'ArrowRight increments by step');
 * await expectEqual(fired.input[0], 55, 'ry:input fires on keyboard');
 * await expectEqual(fired.change[0], 55, 'ry:change ALSO fires on keyboard');
 * ```
 *
 * @tutorial pageup-jumps-10-percent
 * ```js
 * const el = mount('<ry-slider min="0" max="200" value="50"></ry-slider>');
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'PageUp', bubbles: true }));
 * // largeStep = (200 - 0) / 10 = 20 → 50 + 20 = 70
 * await expectEqual(el.value, 70, 'PageUp jumps 10% of range');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'PageDown', bubbles: true }));
 * await expectEqual(el.value, 50, 'PageDown jumps 10% down');
 * ```
 *
 * @tutorial home-and-end-jump-to-extremes
 * ```js
 * const el = mount('<ry-slider min="0" max="100" value="50"></ry-slider>');
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.value, 100);
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(el.value, 0);
 * ```
 *
 * @tutorial disabled-suppresses-keyboard
 * ```js
 * const el = mount('<ry-slider min="0" max="100" value="50" disabled></ry-slider>');
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 50, 'value unchanged');
 * await expectEqual(fired.length, 0);
 * ```
 *
 * @tutorial range-keyboard-prevents-crossover
 * ```js
 * // Drive the start thumb past the end; it should clamp at end.
 * const el = mount('<ry-slider min="0" max="100" start="48" end="50" step="5" range></ry-slider>');
 * const startThumb = el.querySelector('[data-ry-target="thumb-start"]');
 * startThumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.start, 50, 'start clamped at end');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the commit — for a keyboard
 * // step the value never moves and neither ry:input nor ry:change fires.
 * const el = mount('<ry-slider min="0" max="100" step="5" value="50"></ry-slider>');
 * const fired = { input: [], change: [] };
 * el.addEventListener('ry:input', (e) => fired.input.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.value));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * const thumb = el.querySelector('[data-ry-target="thumb-value"]');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(seen.value, 55, 'detail.value is the NEXT value');
 * await expectEqual(seen.previous, 50, 'detail.previous is the current value');
 * await expectEqual(el.value, 50, 'vetoed step leaves the value at 50');
 * await expectEqual(fired.change.length, 0, 'ry:change did not fire');
 * await expectEqual(fired.input.length, 0, 'ry:input did not fire either');
 * thumb.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 55, 'without the veto, the step commits');
 * await expectEqual(fired.change[0], 55);
 * ```
 *
 * @see ry-progress
 * @see ry-rating
 */

import { RyElement } from '../core/ry-element.js';

/** What a commit is about to set: single value, or the range pair. */
type SliderSnapshot = number | { start: number; end: number };

export class RySlider extends RyElement {
  #dragging = false;
  #activeThumb: 'value' | 'start' | 'end' | null = null;
  /** Committed value(s) at drag start — what `previous` is on release, and what a vetoed release snaps back to. */
  #dragPrevious: SliderSnapshot | null = null;
  #track: HTMLElement | null = null;
  #fill: HTMLElement | null = null;
  #thumbs: Map<string, HTMLElement> = new Map();
  #tooltips: Map<string, HTMLElement> = new Map();

  static observedAttributes = ['min', 'max', 'step', 'value', 'start', 'end', 'disabled'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#updatePositions();
  }

  #buildMarkup(): void {
    const isRange = this.hasAttribute('range');
    const showLabels = this.hasAttribute('labeled');
    const showTooltip = this.hasAttribute('tooltip');
    const isVertical = this.hasAttribute('vertical');

    let html = `
      <div data-ry-target="track" class="ry-slider__track">
        <div data-ry-target="fill" class="ry-slider__fill"></div>
    `;

    if (isRange) {
      html += `
        <div data-ry-target="thumb-start" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="Start value">
          ${showTooltip ? '<span data-ry-target="tooltip-start" class="ry-slider__tooltip"></span>' : ''}
        </div>
        <div data-ry-target="thumb-end" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="End value">
          ${showTooltip ? '<span data-ry-target="tooltip-end" class="ry-slider__tooltip"></span>' : ''}
        </div>
      `;
    } else {
      html += `
        <div data-ry-target="thumb-value" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="Value">
          ${showTooltip ? '<span data-ry-target="tooltip-value" class="ry-slider__tooltip"></span>' : ''}
        </div>
      `;
    }

    html += '</div>';

    if (showLabels) {
      html += this.#buildLabels();
    }

    this.innerHTML = html;

    // Cache references
    this.#track = this.$('[data-ry-target="track"]');
    this.#fill = this.$('[data-ry-target="fill"]');

    if (isRange) {
      this.#thumbs.set('start', this.$('[data-ry-target="thumb-start"]')!);
      this.#thumbs.set('end', this.$('[data-ry-target="thumb-end"]')!);
      if (showTooltip) {
        this.#tooltips.set('start', this.$('[data-ry-target="tooltip-start"]')!);
        this.#tooltips.set('end', this.$('[data-ry-target="tooltip-end"]')!);
      }
    } else {
      this.#thumbs.set('value', this.$('[data-ry-target="thumb-value"]')!);
      if (showTooltip) {
        this.#tooltips.set('value', this.$('[data-ry-target="tooltip-value"]')!);
      }
    }
  }

  #buildLabels(): string {
    const min = this.min;
    const max = this.max;
    const customLabels = this.getAttribute('labels')?.split(',');
    const labelType = this.getAttribute('label-type') || 'number';

    let labels: string[] = [];

    if (customLabels) {
      labels = customLabels;
    } else {
      // Show 5 evenly distributed labels (always include min and max)
      const labelCount = 5;
      for (let i = 0; i < labelCount; i++) {
        const ratio = i / (labelCount - 1);
        const val = min + ratio * (max - min);
        if (labelType === 'letter') {
          labels.push(String.fromCharCode(65 + i));
        } else {
          // Round to reasonable precision
          const rounded = Math.round(val * 100) / 100;
          labels.push(String(Number.isInteger(rounded) ? rounded : rounded.toFixed(1)));
        }
      }
    }

    return `
      <div data-ry-target="labels" class="ry-slider__labels">
        ${labels.map(l => `<span>${l}</span>`).join('')}
      </div>
    `;
  }

  #bindEvents(): void {
    // Track click
    if (this.#track) {
      this.on(this.#track, 'pointerdown', this.#handleTrackDown);
    }

    // Thumb events
    for (const [key, thumb] of this.#thumbs) {
      this.on(thumb, 'pointerdown', (e) => this.#handleThumbDown(e, key));
      this.on(thumb, 'keydown', (e) => this.#handleKeydown(e, key));
    }

    // Document events for drag
    this.on(document, 'pointermove', this.#handlePointerMove);
    this.on(document, 'pointerup', this.#handlePointerUp);
  }

  #handleTrackDown = (e: PointerEvent): void => {
    if (this.disabled) return;

    this.#dragPrevious = this.#snapshot();
    const value = this.#valueFromPointer(e);
    const isRange = this.hasAttribute('range');

    if (isRange) {
      // Move closest thumb
      const distToStart = Math.abs(value - this.start);
      const distToEnd = Math.abs(value - this.end);
      if (distToStart < distToEnd) {
        this.start = value;
        this.#activeThumb = 'start';
      } else {
        this.end = value;
        this.#activeThumb = 'end';
      }
    } else {
      this.value = value;
      this.#activeThumb = 'value';
    }

    this.#dragging = true;
    this.#updatePositions();
    this.#emitInput();
  };

  #handleThumbDown = (e: PointerEvent, key: string): void => {
    if (this.disabled) return;
    e.stopPropagation();
    this.#dragPrevious = this.#snapshot();
    this.#dragging = true;
    this.#activeThumb = key as 'value' | 'start' | 'end';
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  #handlePointerMove = (e: PointerEvent): void => {
    if (!this.#dragging || !this.#activeThumb) return;

    const value = this.#valueFromPointer(e);
    const isRange = this.hasAttribute('range');

    if (isRange) {
      if (this.#activeThumb === 'start') {
        // Prevent crossover
        this.start = Math.min(value, this.end);
      } else if (this.#activeThumb === 'end') {
        this.end = Math.max(value, this.start);
      }
    } else {
      this.value = value;
    }

    this.#updatePositions();
    this.#emitInput();
  };

  #handlePointerUp = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#activeThumb = null;
      const previous = this.#dragPrevious ?? this.#snapshot();
      this.#dragPrevious = null;
      // Interception point for the pointer path. The value already moved live
      // (ry:input); a veto snaps it back to where the drag started.
      if (!this.#emitBeforeChange(this.#snapshot(), previous)) {
        this.#restore(previous);
        this.#updatePositions();
        this.#emitInput();
        return;
      }
      this.#emitChange();
    }
  };

  #handleKeydown = (e: KeyboardEvent, key: string): void => {
    if (this.disabled) return;

    const step = this.step || 1;
    const largeStep = (this.max - this.min) / 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        delta = this.hasAttribute('reversed') ? -step : step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        delta = this.hasAttribute('reversed') ? step : -step;
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        delta = this.min - this.#getValueForKey(key);
        break;
      case 'End':
        delta = this.max - this.#getValueForKey(key);
        break;
      default:
        return;
    }

    e.preventDefault();
    const newValue = this.#clamp(this.#getValueForKey(key) + delta);

    // Compute the commit first so it can be vetoed before anything moves.
    let next: SliderSnapshot;
    if (key === 'start') {
      next = { start: Math.min(newValue, this.end), end: this.end };
    } else if (key === 'end') {
      next = { start: this.start, end: Math.max(newValue, this.start) };
    } else {
      next = newValue;
    }
    if (!this.#emitBeforeChange(next, this.#snapshot())) return;

    if (typeof next === 'number') {
      this.value = next;
    } else {
      this.start = next.start;
      this.end = next.end;
    }

    this.#updatePositions();
    this.#emitInput();
    this.#emitChange();
  };

  /** The committed value(s) right now, in the shape `ry:change` reports. */
  #snapshot(): SliderSnapshot {
    return this.hasAttribute('range') ? { start: this.start, end: this.end } : this.value;
  }

  #restore(snap: SliderSnapshot): void {
    if (typeof snap === 'number') {
      this.value = snap;
    } else {
      this.start = snap.start;
      this.end = snap.end;
    }
  }

  /** `ry:beforechange` — detail is the after-event's shape plus `previous`. */
  #emitBeforeChange(next: SliderSnapshot, previous: SliderSnapshot): boolean {
    if (typeof next === 'number') {
      return this.emit('beforechange', { value: next, previous });
    }
    return this.emit('beforechange', { start: next.start, end: next.end, previous });
  }

  #getValueForKey(key: string): number {
    if (key === 'start') return this.start;
    if (key === 'end') return this.end;
    return this.value;
  }

  #valueFromPointer(e: PointerEvent): number {
    if (!this.#track) return this.min;

    const rect = this.#track.getBoundingClientRect();
    const isVertical = this.hasAttribute('vertical');
    const isReversed = this.hasAttribute('reversed');

    let ratio: number;
    if (isVertical) {
      ratio = (rect.bottom - e.clientY) / rect.height;
    } else {
      ratio = (e.clientX - rect.left) / rect.width;
    }

    if (isReversed) ratio = 1 - ratio;
    ratio = Math.max(0, Math.min(1, ratio));

    const rawValue = this.min + ratio * (this.max - this.min);
    return this.#snapToStep(rawValue);
  }

  #snapToStep(value: number): number {
    const step = this.step;
    if (step === 0) return this.#clamp(value);
    const snapped = Math.round((value - this.min) / step) * step + this.min;
    return this.#clamp(snapped);
  }

  #clamp(value: number): number {
    return Math.max(this.min, Math.min(this.max, value));
  }

  #updatePositions(): void {
    const isRange = this.hasAttribute('range');
    const isVertical = this.hasAttribute('vertical');
    const isReversed = this.hasAttribute('reversed');

    const toPercent = (val: number) => {
      let pct = ((val - this.min) / (this.max - this.min)) * 100;
      if (isReversed) pct = 100 - pct;
      return pct;
    };

    if (isRange) {
      const startPct = toPercent(this.start);
      const endPct = toPercent(this.end);
      const lowPct = Math.min(startPct, endPct);
      const highPct = Math.max(startPct, endPct);

      if (this.#fill) {
        if (isVertical) {
          this.#fill.style.bottom = `${lowPct}%`;
          this.#fill.style.height = `${highPct - lowPct}%`;
          this.#fill.style.left = '';
          this.#fill.style.width = '';
        } else {
          this.#fill.style.left = `${lowPct}%`;
          this.#fill.style.width = `${highPct - lowPct}%`;
          this.#fill.style.bottom = '';
          this.#fill.style.height = '';
        }
      }

      const startThumb = this.#thumbs.get('start');
      const endThumb = this.#thumbs.get('end');
      if (startThumb) {
        if (isVertical) {
          startThumb.style.bottom = `${startPct}%`;
          startThumb.style.left = '';
        } else {
          startThumb.style.left = `${startPct}%`;
          startThumb.style.bottom = '';
        }
      }
      if (endThumb) {
        if (isVertical) {
          endThumb.style.bottom = `${endPct}%`;
          endThumb.style.left = '';
        } else {
          endThumb.style.left = `${endPct}%`;
          endThumb.style.bottom = '';
        }
      }

      // Update tooltips
      this.#updateTooltip('start', this.start);
      this.#updateTooltip('end', this.end);

      // Update ARIA
      startThumb?.setAttribute('aria-valuenow', String(this.start));
      startThumb?.setAttribute('aria-valuemin', String(this.min));
      startThumb?.setAttribute('aria-valuemax', String(this.end));
      endThumb?.setAttribute('aria-valuenow', String(this.end));
      endThumb?.setAttribute('aria-valuemin', String(this.start));
      endThumb?.setAttribute('aria-valuemax', String(this.max));
    } else {
      const pct = toPercent(this.value);

      if (this.#fill) {
        if (isVertical) {
          this.#fill.style.bottom = '0';
          this.#fill.style.height = `${pct}%`;
          this.#fill.style.left = '';
          this.#fill.style.width = '';
        } else {
          this.#fill.style.left = '0';
          this.#fill.style.width = `${pct}%`;
          this.#fill.style.bottom = '';
          this.#fill.style.height = '';
        }
      }

      const thumb = this.#thumbs.get('value');
      if (thumb) {
        if (isVertical) {
          thumb.style.bottom = `${pct}%`;
          thumb.style.left = '';
        } else {
          thumb.style.left = `${pct}%`;
          thumb.style.bottom = '';
        }
      }

      // Update tooltip
      this.#updateTooltip('value', this.value);

      // Update ARIA
      thumb?.setAttribute('aria-valuenow', String(this.value));
      thumb?.setAttribute('aria-valuemin', String(this.min));
      thumb?.setAttribute('aria-valuemax', String(this.max));
    }
  }

  #updateTooltip(key: string, value: number): void {
    const tooltip = this.#tooltips.get(key);
    if (tooltip) {
      const decimals = this.step === 0 ? 2 : (String(this.step).split('.')[1]?.length || 0);
      tooltip.textContent = value.toFixed(decimals);
    }
  }

  #emitInput(): void {
    if (this.hasAttribute('range')) {
      this.emit('input', { start: this.start, end: this.end });
    } else {
      this.emit('input', { value: this.value });
    }
  }

  #emitChange(): void {
    if (this.hasAttribute('range')) {
      this.emit('change', { start: this.start, end: this.end });
    } else {
      this.emit('change', { value: this.value });
    }
  }

  // --- Public API ---

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '1');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? String(this.min));
  }
  set value(val: number) {
    this.setAttribute('value', String(this.#clamp(val)));
  }

  get start(): number {
    return parseFloat(this.getAttribute('start') ?? String(this.min));
  }
  set start(val: number) {
    this.setAttribute('start', String(this.#clamp(val)));
  }

  get end(): number {
    return parseFloat(this.getAttribute('end') ?? String(this.max));
  }
  set end(val: number) {
    this.setAttribute('end', String(this.#clamp(val)));
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }
}

customElements.define('ry-slider', RySlider);
